import React, { useEffect, useState } from "react";

import { useGetDatasetCategories } from "api/api-hooks/datasets-hooks";
import DatasetCategoryDropdown from "components/basic/dropdowns/dataset-category/DatasetCategoryDropdown";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import {
  IScheduledTaskPropertyRef,
  InputChangeEvent,
} from "api/model/airflow-task/task-template/TaskTemplateInfo";
import { IPair } from "models/dataset-models";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmPopup } from "primereact/confirmpopup";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { T } from "services/lang/LanguageService";

interface ITaskConfigCategoryTableProps extends IScheduledTaskPropertyRef {
  header: string;
  categories: Record<string, number>;
  onChange: (e: InputChangeEvent<Record<string, number>>) => void;
  bodyClass?: string;
}

interface ICategoryRowModel {
  key: string;
  value: number | undefined;
  newKey: string;
  newValue: number | undefined;
  editMode: boolean;
}

export default function TaskConfigCategoryTable(props: ITaskConfigCategoryTableProps) {
  const [dataArr, setDataArr] = useState<ICategoryRowModel[]>(mapToList(props.categories));
  const [titleMap, setTitleMap] = useState<Record<number, string>>({});

  const categoryQuery = useGetDatasetCategories();

  useEffect(() => {
    const newTitleMap: Record<number, string> = {};
    if (categoryQuery.data) {
      categoryQuery.data.forEach(it => {
        newTitleMap[it.id!!] = it.toString();
      });

      setTitleMap(newTitleMap);
    }
  }, [categoryQuery.data, dataArr]);

  const propagateChangesStateUp = (arr: IPair[]) => {
    const c: Record<string, number> = {};

    arr.forEach(e => {
      if (e.value) c[e.key] = e.value; // Category id must be defined
    });

    props.onChange({ value: c, property: props.property });
  };

  const confirmWrapper = (event: any, confirmHandler: any) => {
    confirmPopup({
      target: event.currentTarget,
      message: T("confirm-popup.are-you-really-want-to-delete-this-category-mapping"),
      icon: "pi pi-exclamation-triangle",
      accept: confirmHandler,
    });
  };

  const showWarn = () => {
    easasAlert({
      summary: T("alert.invalid"),
      detail: T("alert.category-must-be-selected"),
      severity: "warn",
      life: 3000,
    });
  };

  const handleEdit = (row: ICategoryRowModel) => {
    const idx = dataArr.indexOf(row);

    dataArr[idx].editMode = true;
    dataArr[idx].newValue = dataArr[idx].value;
    dataArr[idx].newKey = dataArr[idx].key;
    setDataArr([...dataArr]);
  };

  const handleEditCancel = (row: ICategoryRowModel) => {
    const idx = dataArr.indexOf(row);

    // Check if item is selected, if not remove row
    if (row.newValue == null || row.value == null) {
      setDataArr([...dataArr.filter(item => item.key !== row.key)]);
      return;
    }

    dataArr[idx].editMode = false;
    dataArr[idx].newValue = dataArr[idx].value;
    dataArr[idx].newKey = dataArr[idx].key;
    setDataArr([...dataArr]);
  };

  const handleEditConfirm = (row: ICategoryRowModel) => {
    const idx = dataArr.indexOf(row);

    if (dataArr[idx].newValue === undefined) {
      showWarn();
      return;
    }

    dataArr[idx].editMode = false;
    dataArr[idx].value = dataArr[idx].newValue;
    dataArr[idx].key = dataArr[idx].newKey;

    const updated = [...dataArr];
    setDataArr(updated);
    propagateChangesStateUp(updated);
  };

  const handleAdd = () => {
    const updated = [
      ...dataArr,
      { newKey: "new_category", newValue: undefined, editMode: true } as ICategoryRowModel,
    ];
    setDataArr(updated);
  };

  const handleDelete = (row: ICategoryRowModel) => {
    const filtered = [...dataArr.filter(item => item.key !== row.key)];
    setDataArr(filtered);
    propagateChangesStateUp(filtered);
  };

  const handleValueChange = (e: any, row: ICategoryRowModel) => {
    const idx = dataArr.indexOf(row);
    dataArr[idx].newValue = e;
    setDataArr([...dataArr]);
  };

  const handleKeyChange = (e: any, row: ICategoryRowModel) => {
    const idx = dataArr.indexOf(row);
    dataArr[idx].newKey = e.target.value;
    setDataArr([...dataArr]);
  };

  const headerTemplate = (
    <div className="flex justify-content-between">
      <h4>{props.header}</h4>
      <Button icon="pi pi-plus" className="p-button-success" onClick={() => handleAdd()} />
    </div>
  );

  const keyBodyTemplate = (row: ICategoryRowModel) => {
    return (
      <>
        {!row.editMode && <div>{row.key}</div>}

        {row.editMode && (
          <InputText
            placeholder={T("word.key")}
            value={row.newKey || ""}
            onChange={e => handleKeyChange(e, row)}
          />
        )}
      </>
    );
  };

  const valueBodyTemplate = (row: ICategoryRowModel) => {
    return (
      <>
        {!row.editMode && <div>{titleMap[row.value!!]}</div>}

        {row.editMode && (
          <DatasetCategoryDropdown value={row.newValue} onChange={e => handleValueChange(e, row)} />
        )}
      </>
    );
  };

  const actionBodyTemplate = (row: ICategoryRowModel) => {
    return (
      <>
        {!row.editMode && (
          <Button
            icon="pi pi-pencil"
            className="p-button-rounded p-button-text"
            onClick={() => handleEdit(row)}
          />
        )}

        {row.editMode && (
          <Button
            icon="pi pi-check"
            className="p-button-rounded p-button-text"
            onClick={() => handleEditConfirm(row)}
          />
        )}

        {row.editMode && (
          <Button
            icon="pi pi-times"
            className="p-button-plain p-button-rounded p-button-text"
            onClick={() => handleEditCancel(row)}
          />
        )}

        <Button
          icon="pi pi-trash"
          className="const-del-btn  p-button-rounded p-button-text p-button-danger"
          onClick={e => confirmWrapper(e, () => handleDelete(row))}
        />
      </>
    );
  };

  return (
    <DataTable
      value={dataArr}
      responsiveLayout="scroll"
      header={headerTemplate}
      width="100%"
      className={props.bodyClass}
      size="small"
    >
      <Column header={T("word.key")} body={keyBodyTemplate} style={{ width: "20rem" }} />
      <Column header={T("word.value")} body={valueBodyTemplate} />
      <Column body={actionBodyTemplate} bodyStyle={{ textAlign: "right" }} />
    </DataTable>
  );
}

function mapToList(map: Record<string, number>): ICategoryRowModel[] {
  return Object.keys(map).map(k => {
    return { key: k, value: map[k], editMode: false } as ICategoryRowModel;
  });
}
